

import axiosInstance from "../../CustomAxios/axiosInstance";

// Types
export interface Stock {
    id: number;
    quantity: number;
    sold: number;
    unit_buy_price: number;
    unit_buy_price_ht: string;
    unit_sell_price: number;
    unit_sell_price_ht: string;
    created_at: string;
    updated_at: string;
    product: {
        id: number;
        name: string;
        image: string | null;
        category_name: string;
        brand_name: string;
    };
}

export interface Product {
    id: string;
    name: string;
    category_name?: string;
    brand?: {
        id: string;
        name: string;
    };
}

export interface Arrivage {
    id: number;
    reference: string | null;
    fournisseur_id: number;
    fournisseur_name: string;
    total_buy_price: string;
    total_sell_price: string;
    total_qte: number;
    products_count: number;
    created_at: string;
    stocks: Stock[];
    date: string
}

interface CreateArrivageData {
    fournisseur_id: number
    date: string
    stock_items: Array<{
        product_id: number
        quantity: number
        unit_buy_price: number
        unit_sell_price: number
    }>
}

class StockService {
    /**
     * Get all arrivages (shipments) with their stock details
     * 
     * Each arrivage contains:
     * - Basic information (ID, reference, creation date)
     * - Supplier (fournisseur) details
     * - Financial totals (buy price, sell price)
     * - Stock entries with product information
     * 
     * @returns {Promise} Promise with arrivages data
     */
    async getAllArrivages(): Promise<Arrivage[]> {
        try {
            const response = await axiosInstance.get('/inventory/arrivages');
            
            if (response.data.status === 'success') {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to fetch arrivages');
            }
        } catch (error) {
                        throw error;
        }
    }

    /**
     * Create a new arrivage
     * 
     * @param {object} data - Data for the new arrivage
     * @returns {Promise} Promise with created arrivage data
     */
    async createArrivage(data: CreateArrivageData): Promise<Arrivage> {
        try {
                        const response = await axiosInstance.post('/inventory/arrivages', data);
            
            if (response.data.status === 'success') {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to create arrivage');
            }
        } catch (error: any) {
                        
            // Handle axios errors
            if (error.response) {
                throw new Error(error.response.data.message || 'Failed to create arrivage');
            }
            
            throw error;
        }
    }

    /**
     * Delete an arrivage
     * 
     * @param {number} arrivageId - ID of the arrivage to delete
     * @returns {Promise} Promise with delete confirmation
     */
    async deleteArrivage(arrivageId: number): Promise<void> {
        try {
            const response = await axiosInstance.delete(`/inventory/arrivages/${arrivageId}`);
            
            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Failed to delete arrivage');
            }
        } catch (error: any) {
                        
            // Handle axios errors
            if (error.response) {
                throw new Error(error.response.data.message || 'Failed to delete arrivage');
            }
            
            throw error;
        }
    }

    /**
     * Update a stock entry
     * 
     * @param {number} stockId - ID of the stock to update
     * @param {object} stockData - Data to update
     * @returns {Promise} Promise with updated stock data
     */
    async updateStock(stockId: number, stockData: {
        quantity: number;
        unit_sell_price: number;
    }): Promise<Stock> {
        try {
            const response = await axiosInstance.post(`/inventory/stocks/${stockId}/update`, stockData);
            
            if (response.data.status === 'success') {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to update stock');
            }
        } catch (error: any) {
                        
            // Handle axios errors
            if (error.response) {
                throw new Error(error.response.data.message || 'Failed to update stock');
            }
            
            throw error;
        }
    }

    async getArrivageById(id: number): Promise<Arrivage> {
        try {
            const response = await axiosInstance.get(`/inventory/arrivages/${id}`);
            
            if (response.data.status === 'success') {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to fetch arrivage');
            }
        } catch (error: any) {
                        
            // Handle axios errors
            if (error.response) {
                throw new Error(error.response.data.message || 'Failed to fetch arrivage');
            }
            
            throw error;
        }
    }

    async fetchLowStockProducts(): Promise<any[]> {
        try {
            const response = await axiosInstance.get('/products/low-stock-products');
            return response.data.data;
        } catch (error) {
                        throw error;
        }
    }

    async getStocks(): Promise<Stock[]> {
        try {
            const response = await axiosInstance.get('/inventory/stocks');
            return response.data.data;
        } catch (error) {
                        throw error;
        }
    }
}

export default new StockService();